import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OXBURY_CODE, PRODUCT_CUSTOM_PAYMENT_CODE, PRODUCT_PRICE_CURRENCY } from 'appConstants';
import type { IFieldsSchema } from 'components/DataAccordionColumns/types';
import { useMetadata } from 'contexts/Metadata';
import type { IGenericObject } from 'types';
import { displayAddress, displayDate } from 'utils';

export default function useOrderDetails(
  modifiedAccessor?: IGenericObject,
  externalDeps?: IGenericObject,
  isSummary: boolean = false,
): IFieldsSchema[] {
  const { t } = useTranslation('CreateOrder');

  const {
    state: metadataState,
    selectors: { selectTransportMethod, selectPaymentTerm },
  } = useMetadata({ loadTransportMethodsData: true, loadPaymentTermsData: true });

  const accessor = useMemo(
    () => ({
      productName: 'productData.packagedItemDescription',
      productDescription: 'productData.packageDescription',
      quantity: 'packagedItemQty',
      price: 'packagedItemPrice',
      paymentTermsCode: 'paymentTerms.paymentTermsCode',
      paymentDueDate: 'paymentTerms.paymentDueDate',
      businessUserName: 'businessUser.name',
      businessUserContactNumber: 'businessUser.contactNumber',
      transportMethod: 'transportMethod',
      startDate: 'startDate',
      endDate: 'endDate',
      addressName: 'addressName',
      productNotes: 'packagedItemNotes',
      ...modifiedAccessor,
    }),
    [modifiedAccessor],
  );

  const displayTransportMethod = useCallback(
    (value) => {
      const transportMethod = selectTransportMethod(metadataState, value);

      return transportMethod ? transportMethod.label : '';
    },
    [metadataState, selectTransportMethod],
  );

  const displayPaymentTerm = useCallback(
    (paymentTermsCode) => {
      const paymentTerm = selectPaymentTerm(metadataState, paymentTermsCode);

      return paymentTerm ? paymentTerm.paymentDescription : '';
    },
    [metadataState, selectPaymentTerm],
  );

  return useMemo(
    () => [
      {
        fields: [
          {
            label: t('OrderSummary.fields.productName'),
            accessor: accessor.productName,
            hidden: isSummary,
          },
          {
            label: t('OrderSummary.fields.productDescription'),
            accessor: accessor.productDescription,
          },
          {
            label: t('OrderSummary.fields.bulkQuantity'),
            accessor: accessor.quantity,
          },
          {
            label: t('OrderSummary.fields.price'),
            accessor: accessor.price,
            displayValue: (value) => `${PRODUCT_PRICE_CURRENCY}${value}`,
          },
          {
            label: t('OrderSummary.fields.paymentTerms'),
            accessor: accessor.paymentTermsCode,
            displayValue: (value) => displayPaymentTerm(value),
            hidden: () => {
              const { isDebtOwner = false } = externalDeps || {};
              return isDebtOwner;
            },
          },
          {
            label: t('OrderSummary.fields.paymentDue'),
            accessor: accessor.paymentDueDate,
            hidden: ({ paymentTerms }) => {
              const { paymentTermsCode = '' } = paymentTerms || {};
              const { isDebtOwner = false } = externalDeps || {};
              return isDebtOwner || paymentTermsCode !== PRODUCT_CUSTOM_PAYMENT_CODE;
            },
            displayValue: (value) => displayDate(value),
          },
          {
            label: t('OrderSummary.fields.businessName'),
            accessor: accessor.businessUserName,
            hidden: ({ paymentTerms }) => {
              const { paymentTermsCode = '' } = paymentTerms || {};
              const { isDebtOwner = false } = externalDeps || {};
              return isDebtOwner || !paymentTermsCode.startsWith(OXBURY_CODE);
            },
          },
          {
            label: t('OrderSummary.fields.businessContact'),
            accessor: accessor.businessUserContactNumber,
            hidden: ({ paymentTerms }) => {
              const { paymentTermsCode = '' } = paymentTerms || {};
              const { isDebtOwner = false } = externalDeps || {};
              return isDebtOwner || !paymentTermsCode.startsWith(OXBURY_CODE);
            },
          },
        ],
      },
      {
        fields: [
          {
            label: t('OrderSummary.fields.transportMethod'),
            accessor: accessor.transportMethod,
            displayValue: (value) => displayTransportMethod(value),
          },
          {
            label: t('OrderSummary.fields.deliveryStartDate'),
            accessor: accessor.startDate,
            displayValue: (value) => displayDate(value),
          },
          {
            label: t('OrderSummary.fields.deliveryEndDate'),
            accessor: accessor.endDate,
            displayValue: (value) => displayDate(value),
          },
          {
            label: ({ transportMethod = '' }) =>
              t(`OrderSummary.fields.deliveryAddress${transportMethod}`),
            accessor: accessor.addressName,
            displayValue: (value, { addressData }) => value || displayAddress(addressData),
          },
          {
            label: t('OrderSummary.fields.productNotes'),
            accessor: accessor.productNotes,
          },
        ],
      },
    ],
    [displayPaymentTerm, displayTransportMethod, t, accessor, isSummary, externalDeps],
  );
}
